import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

type SelectVariant = 'picker' | 'search';

type SelectBlockOptionGroup = {
  label: string;
  options: string[];
};

export function SelectBlock({
  title,
  options,
  optionGroups,
  value,
  onChange,
  variant = 'search',
  placeholder = 'Начните вводить…',
  allowCustom = true,
  isLoading = false,
  className,
  searchQuery,
  onSearchQueryChange,
}: {
  title: string;
  options?: string[];
  optionGroups?: SelectBlockOptionGroup[];
  value: string;
  onChange: (value: string) => void;
  variant?: SelectVariant;
  placeholder?: string;
  allowCustom?: boolean;
  isLoading?: boolean;
  className?: string;
  searchQuery?: string;
  onSearchQueryChange?: (q: string) => void;
}) {
  const [open, setOpen] = useState(false);

  const [innerQuery, setInnerQuery] = useState(value);
  const query = searchQuery ?? innerQuery;

  useEffect(() => {
    if (variant === 'search' && searchQuery === undefined) {
      setInnerQuery(value);
    }
  }, [value, variant, searchQuery]);

  const flatOptions = useMemo(
    () => options ?? optionGroups?.flatMap((group) => group.options) ?? [],
    [optionGroups, options]
  );

  const filtered = useMemo(() => {
    if (variant !== 'search') {
      return flatOptions;
    }

    const q = (query ?? '').trim().toLowerCase();
    if (!q) {
      return flatOptions;
    }
    return flatOptions.filter((opt) => opt.toLowerCase().includes(q));
  }, [flatOptions, query, variant]);

  const filteredGroups = useMemo(() => {
    if (!optionGroups?.length) {
      return [];
    }

    const q = (query ?? '').trim().toLowerCase();
    return optionGroups
      .map((group) => ({
        ...group,
        options:
          variant === 'search' && q
            ? group.options.filter((opt) => opt.toLowerCase().includes(q))
            : group.options,
      }))
      .filter((group) => group.options.length > 0);
  }, [optionGroups, query, variant]);

  const commit = (next: string) => {
    onChange(next);

    if (variant === 'search') {
      if (onSearchQueryChange) {
        onSearchQueryChange(next);
      } else {
        setInnerQuery(next);
      }
    }

    setOpen(false);
  };

  const showUseCustom =
    variant === 'search' &&
    allowCustom &&
    (query ?? '').trim().length > 0 &&
    !flatOptions.some(
      (o) => o.toLowerCase() === (query ?? '').trim().toLowerCase()
    );

  const renderFlatOptions = () =>
    filtered.map((opt) => (
      <button
        type="button"
        key={opt}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => commit(opt)}
        className="
          w-full text-left px-3 my-[10px]
          text-[12px] leading-[130%] tracking-[-0.5px] font-medium
          text-[#1E2C50] hover:bg-muted transition
        "
      >
        {opt}
      </button>
    ));

  const renderGroupedOptions = () =>
    filteredGroups.map((group) => (
      <div key={group.label}>
        <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[#6B7A99]">
          {group.label}
        </p>
        {group.options.map((opt) => (
          <button
            type="button"
            key={`${group.label}-${opt}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => commit(opt)}
            className="
              w-full text-left px-3 my-[10px]
              text-[12px] leading-[130%] tracking-[-0.5px] font-medium
              text-[#1E2C50] hover:bg-muted transition
            "
          >
            {opt}
          </button>
        ))}
      </div>
    ));

  return (
    <div className={className}>
      <p className="text-[12px] leading-[130%] tracking-[-0.5px] font-medium mb-[10px] pt-[9px] px-[13px]">
        {title}
      </p>

      <div className="rounded-[10px] border border-[#3B4D7F] overflow-hidden">
        {variant === 'search' ? (
          <div className="w-full flex items-center justify-between px-3 mt-[8px] mb-[4px] gap-2">
            <input
              value={query ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                if (onSearchQueryChange) {
                  onSearchQueryChange(v);
                } else {
                  setInnerQuery(v);
                }

                if (!open) {
                  setOpen(true);
                }
              }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              className="
                w-full bg-transparent outline-none
                text-[16px] leading-[130%] tracking-[-0.5px] font-medium text-[#1E2C50] placeholder-text-[16px]
              "
            />

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="shrink-0 text-black"
              aria-label="Toggle"
            >
              <ChevronDown
                size={16}
                className={`transition-transform duration-300 ${open ? 'rotate-180' : 'rotate-0'}`}
              />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="
              w-full flex items-center justify-between
              px-3 mt-[8px] mb-[4px]
              text-left text-[12px] leading-[130%] tracking-[-0.5px] font-medium
            "
          >
            <span className="text-[#1E2C50] text-[16px]">{value}</span>
            <ChevronDown
              size={16}
              className={`transition-transform duration-300 ${open ? 'rotate-180' : 'rotate-0'}`}
            />
          </button>
        )}

        {open && <div className="mx-3 h-px bg-border" />}

        <div
          className={`
            transition-all duration-300 ease-out
            ${open ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}
            overflow-hidden
          `}
        >
          {isLoading && (
            <div className="px-3 my-[10px] text-[12px] font-medium text-black">
              Загрузка…
            </div>
          )}

          {!isLoading && showUseCustom && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => commit((query ?? '').trim())}
              className="
                w-full text-left
                px-3 my-[10px]
                text-[12px] leading-[130%] tracking-[-0.5px] font-medium
                text-black hover:bg-muted transition
              "
            >
              Использовать: {(query ?? '').trim()}
            </button>
          )}

          {!isLoading &&
            (filteredGroups.length > 0 ? (
              renderGroupedOptions()
            ) : filtered.length > 0 ? (
              renderFlatOptions()
            ) : (
              <div className="px-3 my-[10px] text-[12px] font-medium text-black">
                Ничего не найдено
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
